import { useCallback, useEffect, useState } from 'react';
import {
  FOOD_OFFSET_MINUTES, MEASURES, MEASURE_UNITS, checkReading, defaultMeasureUnit, hasSecondValue, localDateAt,
  measurementsCsv, readingText, type Measure,
} from '@drbell/core';
import { api, type PatientSummary } from '../api.ts';

/**
 * Measurements the doctor asked for — docs/33 D1, parity with the phone.
 *
 * A measurement is an item of kind `measurement` on the medicine engine: it
 * reminds at the person's meal times and its reminder is answered by typing
 * the number. Here a caregiver sets one up, types a reading, reads the list
 * and downloads it for the doctor. Nothing says what a number means: no
 * chart, no colour, no goal (docs/05 anti-goals).
 *
 * Shown only while `measurements.enabled` is on (admin console → Flags); the
 * server refuses a reading otherwise.
 */
const KIND: Record<Measure, string> = {
  bp: 'Blood pressure',
  sugar: 'Blood sugar',
  weight: 'Weight',
  temperature: 'Temperature',
  spo2: 'Oxygen (SpO₂)',
  pulse: 'Pulse',
  other: 'Something else',
};

type Meal = 'breakfast' | 'lunch' | 'dinner' | 'bedtime';
const WHEN: Array<{ id: string; meal: Meal; offset: number; label: string }> = [
  { id: 'b-breakfast', meal: 'breakfast', offset: FOOD_OFFSET_MINUTES.before_food, label: 'Before breakfast' },
  { id: 'a-breakfast', meal: 'breakfast', offset: FOOD_OFFSET_MINUTES.after_food, label: 'After breakfast' },
  { id: 'b-lunch', meal: 'lunch', offset: FOOD_OFFSET_MINUTES.before_food, label: 'Before lunch' },
  { id: 'a-lunch', meal: 'lunch', offset: FOOD_OFFSET_MINUTES.after_food, label: 'After lunch' },
  { id: 'b-dinner', meal: 'dinner', offset: FOOD_OFFSET_MINUTES.before_food, label: 'Before dinner' },
  { id: 'a-dinner', meal: 'dinner', offset: FOOD_OFFSET_MINUTES.after_food, label: 'After dinner' },
  { id: 'bedtime', meal: 'bedtime', offset: 0, label: 'Bedtime' },
];

export interface MeasureItem {
  medicationId: string;
  name: string;
  measure: Measure;
  unit: string;
}

interface Row {
  id: string;
  medicationId: string;
  name: string | null;
  measure: Measure;
  unit: string;
  value: number;
  value2: number | null;
  measuredAt: number;
  note: string | null;
}

/** An entry of `GET /patients/:id/medications?kind=measurement` (stopped ones are not listed). */
interface Item {
  medication: { id: string; name: string; measure: Measure | null; measureUnit: string | null };
}

const input = 'min-h-11 rounded-lg border border-(--line) bg-transparent px-3 text-sm';
const chip = (on: boolean) =>
  `min-h-11 rounded-full border px-3 text-sm ${on ? 'border-(--accent) bg-(--accent) text-white' : 'border-(--line) hover:border-(--accent)'}`;

/**
 * The boxes for a reading: one number, or a blood pressure's two. Used on
 * this tab and on Today, where it answers the reminder that is due.
 */
export function ReadingForm({ patientId, item, doseEventId, onDone, onCancel }: {
  patientId: string;
  item: MeasureItem;
  doseEventId?: string;
  onDone: (reading: string) => void;
  onCancel: () => void;
}) {
  const two = hasSecondValue(item.measure);
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const value = Number(top.replace(',', '.'));
    const value2 = two ? Number(bottom.replace(',', '.')) : null;
    const reading = { measure: item.measure, unit: item.unit, value, value2 };
    if (!top.trim() || (two && !bottom.trim()) || !checkReading(reading).ok) {
      setError('Check the number: it cannot be that.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/patients/${patientId}/measurements`, {
        medicationId: item.medicationId, measure: item.measure, unit: item.unit, value,
        ...(two ? { value2 } : {}), ...(doseEventId ? { doseEventId } : {}), ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone(readingText(reading));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="basis-full space-y-3 rounded-lg border border-(--line) p-3 text-sm"
      onSubmit={(e) => { e.preventDefault(); void save(); }}
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-(--muted)">{two ? 'Top number' : 'Reading'}</span>
          <input autoFocus inputMode="decimal" value={top} onChange={(e) => setTop(e.target.value.slice(0, 8))} className={`${input} w-28`} aria-label={`${item.name}: ${two ? 'top number' : 'reading'}`} />
        </label>
        {two && (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-(--muted)">Bottom number</span>
            <input inputMode="decimal" value={bottom} onChange={(e) => setBottom(e.target.value.slice(0, 8))} className={`${input} w-28`} aria-label={`${item.name}: bottom number`} />
          </label>
        )}
        <span className="pb-3 text-(--muted)">{item.unit}</span>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--muted)">Note (optional)</span>
        <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} className={input} />
      </label>
      {error && <p role="alert" className="text-red-500">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="min-h-11 rounded-lg bg-(--accent) px-4 font-medium text-white disabled:opacity-40">
          {busy ? 'Saving…' : 'Type the reading'}
        </button>
        <button type="button" onClick={onCancel} className="min-h-11 rounded-lg border border-(--line) px-4 hover:border-(--accent)">
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddMeasurement({ patient, onSaved, onCancel }: { patient: PatientSummary; onSaved: () => void; onCancel: () => void }) {
  const [measure, setMeasure] = useState<Measure>('bp');
  const [name, setName] = useState(KIND.bp);
  const [named, setNamed] = useState(false);
  const [unit, setUnit] = useState(defaultMeasureUnit('bp'));
  const [when, setWhen] = useState<string[]>(['b-breakfast']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = (m: Measure) => {
    setMeasure(m);
    setUnit(defaultMeasureUnit(m));
    if (!named) setName(m === 'other' ? '' : KIND[m]);
  };

  const save = async () => {
    if (!name.trim() || !unit.trim() || when.length === 0) {
      setError('Give it a name, a unit and at least one time.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/patients/${patient.id}/medications`, {
        name: name.trim(),
        kind: 'measurement',
        measure,
        measureUnit: unit.trim(),
        form: 'other',
        // A reminder, not an alarm, and nobody told if it is missed: a measurement is not a dose.
        alertStyle: 'notify',
        escalates: false,
        schedule: {
          type: 'meal_relative',
          anchors: WHEN.filter((w) => when.includes(w.id)).map((w) => ({ meal: w.meal, offsetMinutes: w.offset })),
        },
        foodRelation: 'any',
        startDate: localDateAt(Date.now(), patient.timezone),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const units = MEASURE_UNITS[measure];
  return (
    <form className="space-y-4 rounded-xl border border-(--line) p-4" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <h2 className="font-medium">Add a measurement</h2>
      <fieldset>
        <legend className="mb-2 text-xs text-(--muted)">What the doctor asked for</legend>
        <div className="flex flex-wrap gap-2">
          {MEASURES.map((m) => (
            <button type="button" key={m} aria-pressed={measure === m} onClick={() => pick(m)} className={chip(measure === m)}>{KIND[m]}</button>
          ))}
        </div>
      </fieldset>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--muted)">Name</span>
        <input value={name} onChange={(e) => { setName(e.target.value.slice(0, 60)); setNamed(true); }} className={input} />
      </label>
      <fieldset>
        <legend className="mb-2 text-xs text-(--muted)">Measured in</legend>
        {units.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {units.map((u) => (
              <button type="button" key={u} aria-pressed={unit === u} onClick={() => setUnit(u)} className={chip(unit === u)}>{u}</button>
            ))}
          </div>
        ) : (
          <input value={unit} onChange={(e) => setUnit(e.target.value.slice(0, 20))} placeholder="the doctor's unit" className={input} />
        )}
      </fieldset>
      <fieldset>
        <legend className="mb-2 text-xs text-(--muted)">When, by their own meal times</legend>
        <div className="flex flex-wrap gap-2">
          {WHEN.map((w) => {
            const on = when.includes(w.id);
            return (
              <button type="button" key={w.id} aria-pressed={on} onClick={() => setWhen(on ? when.filter((x) => x !== w.id) : [...when, w.id])} className={chip(on)}>
                {w.label}
              </button>
            );
          })}
        </div>
      </fieldset>
      {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40">
          {busy ? 'Saving…' : 'Save this measurement'}
        </button>
        <button type="button" onClick={onCancel} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)">Cancel</button>
      </div>
    </form>
  );
}

export function Measurements({ patient, canRecord, canManage }: { patient: PatientSummary; canRecord: boolean; canManage: boolean }) {
  const [items, setItems] = useState<Array<Item['medication']> | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        api.get<{ medications: Item[] }>(`/api/patients/${patient.id}/medications?kind=measurement`),
        api.get<{ measurements: Row[] }>(`/api/patients/${patient.id}/measurements`),
      ]);
      setItems(m.medications.map((e) => e.medication).filter((i) => i.measure));
      setRows(r.measurements);
    } catch (e) {
      setError(String(e));
    }
  }, [patient.id]);

  useEffect(() => { void load(); }, [load]);

  const when = (ms: number) =>
    new Date(ms).toLocaleString('en-IN', { timeZone: patient.timezone, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  const text = (r: Row) => readingText({ measure: r.measure, unit: r.unit, value: r.value, value2: r.value2 });

  const download = () => {
    const csv = measurementsCsv(['When', 'What', 'Reading', 'Note'], rows.map((r) => ({
      at: new Date(r.measuredAt).toLocaleString('sv-SE', { timeZone: patient.timezone }).slice(0, 16),
      name: r.name ?? '',
      reading: text(r),
      note: r.note,
    })));
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'drbell-readings.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  if (!items) return <p className="text-(--muted)">Loading…</p>;

  return (
    <section className="space-y-6">
      <p className="text-sm text-(--muted)">
        What the doctor asked {patient.isSelf ? 'you' : 'them'} to measure and write down. DrBell reminds at those times and keeps
        the numbers exactly as typed; it never says what they mean.
      </p>
      {saved && <p className="text-sm font-medium text-(--accent)">✓ Saved: {saved}</p>}

      {items.length > 0 && (
        <ul className="divide-y divide-(--line) rounded-xl border border-(--line)">
          {items.map((i) => {
            const last = rows.find((r) => r.medicationId === i.id);
            const item: MeasureItem = { medicationId: i.id, name: i.name, measure: i.measure!, unit: i.measureUnit ?? defaultMeasureUnit(i.measure!) };
            return (
              <li key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{i.name}</div>
                  <div className="text-xs text-(--muted)">{last ? `Last: ${text(last)}, ${when(last.measuredAt)}` : 'No readings yet'}</div>
                </div>
                {canRecord && recording !== i.id && (
                  <button onClick={() => { setSaved(null); setRecording(i.id); }} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)">
                    Type a reading
                  </button>
                )}
                {recording === i.id && (
                  <ReadingForm
                    patientId={patient.id}
                    item={item}
                    onDone={(reading) => { setRecording(null); setSaved(reading); void load(); }}
                    onCancel={() => setRecording(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canManage && (adding ? (
        <AddMeasurement patient={patient} onSaved={() => { setAdding(false); void load(); }} onCancel={() => setAdding(false)} />
      ) : (
        <button
          onClick={() => { setSaved(null); setAdding(true); }}
          className={items.length > 0 ? 'min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)' : 'min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white'}
        >
          Add a measurement
        </button>
      ))}

      <div>
        <h2 className="mb-2 text-sm font-medium">Readings</h2>
        {rows.length === 0 ? (
          <p className="text-sm text-(--muted)">No readings in the last 90 days.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-(--muted)">
                <th className="font-normal">When</th>
                <th className="font-normal">What</th>
                <th className="font-normal">Reading</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-(--line) align-top">
                  <td className="py-2 pr-2 text-(--muted)">{when(r.measuredAt)}</td>
                  <td className="py-2 pr-2">{r.name}</td>
                  <td className="py-2 tabular-nums">{text(r)}{r.note && <div className="text-xs text-(--muted)">{r.note}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rows.length > 0 && (
        <button onClick={download} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)">
          Download the readings as a spreadsheet
        </button>
      )}
    </section>
  );
}
